import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  X as XIcon,
  Users,
  Wallet,
  Sparkles,
  Building2,
  ShieldCheck,
  HeadphonesIcon,
  Handshake,
  Compass,
  Globe2,
  Award,
  Calculator,
  Banknote,
  Scale,
  Briefcase,
  ClipboardList,
  ExternalLink,
  Landmark,
  Heart,
  ShoppingBag,
  Code2,
  Home as HomeIcon,
  Megaphone,
  LineChart,
  User,
  Network,
} from "lucide-react";
import ConnectionVisual from "@/components/ConnectionVisual";
import CtaBand from "@/components/CtaBand";
import Seo, { faqJsonLd } from "@/components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  TRUST_POINTS,
  PAIN_POINTS,
  SOLUTIONS,
  SERVICES,
  HOW_IT_WORKS,
  PROGRESSION,
  EXAMPLE_TEAMS,
  TARGET_MARKETS,
  ADDITIONAL_MARKETS,
  ALSO_FOR,
  ROLES,
  INCLUDED_WITH_HIRE,
  ADD_ON,
  PRICING,
  WHY,
  INDIA_ADVANTAGE,
  ECOSYSTEM,
  FAQS,
} from "@/lib/content";

const Home = () => {
  return (
    <>
      <Seo
        title="Bright EOR | Build Your India Team Without Building an Indian Company"
        description="Hire one dedicated employee or build a complete offshore function. Bright EOR recruits, employs, equips and supports India-based professionals who work exclusively for your business."
        path="/"
        jsonLd={faqJsonLd(FAQS)}
      />
      <Hero />
      <TrustStrip />
      <Progression />
      <ProblemSolution />
      <WhatWeDo />
      <ManagedTeamsTeaser />
      <HowItWorksSec />
      <WhoItsFor />
      <RolesSec />
      <IncludedWithHire />
      <OfficeSetup />
      <PricingSec />
      <WhyBright />
      <IndiaAdvantage />
      <BrandEcosystem />
      <FaqSec />
      <CtaBand />
    </>
  );
};

/* ================= HERO ================= */
const Hero = () => (
  <section className="relative overflow-hidden bg-navy text-white" data-testid="home-hero">
    <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
    <div className="absolute -top-52 -right-40 w-[560px] h-[560px] rounded-full bg-aqua/20 blur-[100px]" aria-hidden />
    <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-aqua/10 blur-[100px]" aria-hidden />

    <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12 pt-16 pb-24 lg:pt-24 lg:pb-32">
      <div className="grid gap-14 lg:grid-cols-2 lg:gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-xl px-3.5 py-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-aqua animate-node-pulse" />
              <span className="relative rounded-full bg-aqua h-2 w-2" />
            </span>
            <span className="text-xs font-medium tracking-tight text-white/90">Part of Brightridge Partners</span>
          </div>

          <h1 className="font-display mt-6 text-[44px] leading-[1.03] tracking-tight font-semibold sm:text-6xl lg:text-[68px]">
            Build your India team <br className="hidden md:block" />
            <span className="text-aqua">without building</span> an Indian company.
          </h1>

          <p className="mt-6 max-w-xl text-lg text-white/75 leading-relaxed">
            Hire one dedicated employee or build a complete offshore function. Bright EOR recruits, employs, equips and supports India-based professionals who work exclusively for your business.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-aqua px-6 py-3.5 text-[15px] font-medium text-navy transition-all hover:shadow-aqua-glow"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/managed-teams"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.04] px-6 py-3.5 text-[15px] font-medium text-white hover:bg-white/10 transition-colors"
            >
              Plan My India Team
            </Link>
          </div>

          <div className="mt-10 flex items-center gap-3 text-sm text-white/60">
            <div className="h-px w-8 bg-aqua/60" />
            <span className="tracking-tight">UK-led support. India-based talent. One managed process.</span>
          </div>
        </div>

        <div className="relative">
          <ConnectionVisual />
        </div>
      </div>
    </div>
  </section>
);

/* ================= TRUST STRIP ================= */
const TrustStrip = () => (
  <section className="bg-navy-50 border-y border-slate-100" data-testid="trust-strip">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 py-10">
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
        {TRUST_POINTS.map((p, i) => (
          <div key={p.title} className="flex gap-4">
            <div className="relative shrink-0">
              <div className="w-9 h-9 rounded-full bg-aqua/15 border border-aqua/30 flex items-center justify-center">
                <span className="w-2 h-2 rounded-full bg-aqua" />
              </div>
              {i < TRUST_POINTS.length - 1 && (
                <div className="hidden lg:block absolute left-full top-1/2 w-16 h-px border-t border-dashed border-aqua/30" />
              )}
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-navy tracking-tight">{p.title}</h3>
              <p className="mt-1 text-[14px] text-slateink leading-relaxed">{p.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ================= PROBLEM / SOLUTION ================= */
const ProblemSolution = () => (
  <section className="py-20 lg:py-28" data-testid="problem-solution">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">The problem</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy leading-[1.05] tracking-tight">
          Hiring in India should not <br className="hidden md:block" /> mean setting up in India.
        </h2>
        <p className="mt-6 text-lg text-slateink leading-relaxed">
          India gives UK businesses access to skilled, ambitious and cost-effective remote talent. But hiring properly can quickly become complicated if you do not have the right structure — employment setup, contracts, payroll, statutory costs, onboarding, equipment, communication and day-to-day remote working expectations.
        </p>
        <p className="mt-4 text-lg text-slateink leading-relaxed">
          Bright EOR gives you a simpler way to hire India-based staff, with a UK-led team supporting the process from setup to ongoing management.
        </p>
      </div>

      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl bg-navy-50 border border-slate-200 p-8 lg:p-10">
          <p className="text-xs uppercase tracking-[0.22em] text-slatemuted font-semibold">Without a partner</p>
          <h3 className="font-display mt-3 text-2xl font-semibold text-navy">You end up managing all of this</h3>
          <ul className="mt-6 space-y-3">
            {PAIN_POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-[15px] text-slateink">
                <span className="mt-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border border-slate-300">
                  <XIcon className="h-3 w-3 text-slatemuted" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-3xl bg-navy text-white p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-aqua/20 blur-3xl" aria-hidden />
          <p className="relative text-xs uppercase tracking-[0.22em] text-aqua font-semibold">With Bright EOR</p>
          <h3 className="relative font-display mt-3 text-2xl font-semibold">We handle the structure. You focus on the work.</h3>
          <ul className="relative mt-6 space-y-3">
            {SOLUTIONS.map((s) => (
              <li key={s} className="flex items-start gap-3 text-[15px] text-white/90">
                <span className="mt-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-aqua text-navy">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {s}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* ================= WHAT WE DO ================= */
const WhatWeDo = () => (
  <section className="bg-navy-50 py-20 lg:py-28" data-testid="services">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">What we do</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
            Four service levels. <span className="text-aqua">One managed process.</span>
          </h2>
          <p className="mt-5 text-lg text-slateink leading-relaxed">
            From a single dedicated hire to a complete offshore function or a dedicated India office — start where it makes sense and scale when it works.
          </p>
        </div>
        <Link
          to="/services"
          className="group inline-flex items-center gap-2 text-navy font-medium hover:text-aqua transition-colors"
          data-testid="services-cta"
        >
          View all services
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => {
          const Icon = [User, Users, Network, Building2][i];
          return (
            <div
              key={s.slug}
              className="group relative rounded-3xl bg-white border border-slate-200 p-7 shadow-card-soft hover:shadow-card-hover hover:border-aqua/40 hover:-translate-y-1 transition-all duration-300"
              data-testid={`service-card-${s.slug}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-aqua/15 flex items-center justify-center text-navy group-hover:bg-aqua transition-colors">
                <Icon className="h-5 w-5" />
              </div>
              <p className="mt-5 text-[11px] uppercase tracking-[0.16em] text-aqua font-semibold">{s.level}</p>
              <h3 className="font-display mt-1 text-xl font-semibold text-navy tracking-tight leading-tight">{s.title}</h3>
              <p className="mt-2 text-[14px] text-slateink leading-relaxed">{s.tagline}</p>
              <Link
                to={s.slug === "team-pod" || s.slug === "offshore-function" ? "/managed-teams" : "/services"}
                className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-navy group-hover:text-aqua transition-colors"
              >
                Learn more <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ================= HOW IT WORKS (Sourcing process) ================= */
const HowItWorksSec = () => (
  <section className="py-20 lg:py-28" data-testid="how-it-works">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">How it works</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          We shortlist. <span className="text-aqua">You choose.</span>
        </h2>
        <p className="mt-6 text-lg text-slateink leading-relaxed">
          Unlike outsourcing agencies that hand you the person they&apos;ve got, Bright EOR sources and screens candidates for your role — then shares you a curated shortlist to interview and choose from.
        </p>
      </div>

      <div className="relative mt-14">
        <div className="hidden lg:block absolute top-8 left-[8%] right-[8%] h-px border-t-2 border-dashed border-aqua/30" aria-hidden />
        <div className="grid gap-8 lg:grid-cols-4 lg:gap-6">
          {HOW_IT_WORKS.map((step) => (
            <div key={step.step} className="relative" data-testid={`step-${step.step}`}>
              <div className="relative z-10 flex flex-col items-start">
                <div className="w-16 h-16 rounded-2xl bg-navy text-white font-display text-xl font-semibold flex items-center justify-center shadow-card-soft">
                  {step.step}
                </div>
                <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-card-soft hover:border-aqua/40 hover:shadow-card-hover transition-all">
                  <h3 className="font-display text-lg font-semibold text-navy tracking-tight">{step.title}</h3>
                  <p className="mt-2 text-[14px] text-slateink leading-relaxed">{step.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ================= WHO IT'S FOR (Target markets w/ roles) ================= */
const industryIcons = { calc: Calculator, banknote: Banknote, scale: Scale, briefcase: Briefcase, globe: Globe2 };
const addlIcons = { heart: Heart, shopping: ShoppingBag, code: Code2, home: HomeIcon, megaphone: Megaphone, chart: LineChart };
const WhoItsFor = () => (
  <section className="bg-navy-50 py-20 lg:py-28" data-testid="who-its-for">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Who it&apos;s for</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          Built for UK businesses ready to scale — <span className="text-aqua">without scaling UK payroll.</span>
        </h2>
        <p className="mt-6 text-lg text-slateink leading-relaxed">
          These five UK-based industries are our priority markets — where India-based remote support unlocks the biggest wins on capacity, margin and speed.
        </p>
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {TARGET_MARKETS.map((m, i) => {
          const Icon = industryIcons[m.icon] || Briefcase;
          return (
            <div
              key={m.industry}
              className="group relative rounded-3xl bg-white border border-slate-200 p-7 hover:border-aqua/40 hover:-translate-y-1 hover:shadow-card-hover transition-all"
              data-testid={`market-${i}`}
            >
              <div className="absolute top-5 right-5 rounded-full bg-aqua/15 text-aqua px-2.5 py-1 text-[10px] font-semibold tracking-[0.14em] uppercase">
                Priority
              </div>
              <div className="w-11 h-11 rounded-2xl bg-aqua/15 text-navy flex items-center justify-center group-hover:bg-aqua transition-colors">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-navy tracking-tight leading-snug">{m.industry}</h3>
              <p className="mt-2 text-[14px] text-slateink leading-relaxed">{m.body}</p>
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slatemuted font-semibold">Example roles</p>
                <ul className="mt-3 space-y-1.5">
                  {m.roles.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-[13.5px] text-slateink">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-aqua shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-16">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px flex-1 bg-slate-200" />
          <p className="text-xs uppercase tracking-[0.22em] text-slatemuted font-semibold">Also serving</p>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ADDITIONAL_MARKETS.map((m, i) => {
            const Icon = addlIcons[m.icon] || Briefcase;
            return (
              <div
                key={m.industry}
                className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-aqua/40 hover:shadow-card-soft transition-all flex items-start gap-4"
                data-testid={`additional-market-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-aqua/10 text-navy flex items-center justify-center shrink-0">
                  <Icon className="h-4.5 w-4.5" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-display text-[15.5px] font-semibold text-navy tracking-tight leading-snug">{m.industry}</p>
                  <p className="mt-1 text-[13.5px] text-slateink leading-relaxed">{m.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-10 rounded-3xl border border-aqua/25 bg-aqua/5 p-6 lg:p-7">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slatemuted">Also great for</p>
          <div className="flex flex-wrap gap-2">
            {ALSO_FOR.map((a) => (
              <span
                key={a}
                data-testid={`also-for-${a.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-aqua/30 text-navy px-3.5 py-1.5 text-[13px] font-medium"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-aqua" />
                {a}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ================= ROLES GRID ================= */
const RolesSec = () => (
  <section className="py-20 lg:py-28" data-testid="roles">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Roles</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
            Roles we can help you hire and support.
          </h2>
        </div>
        <Link to="/roles" className="group inline-flex items-center gap-2 text-navy font-medium hover:text-aqua transition-colors">
          Explore all roles <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      <div className="mt-10 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {ROLES.map((role) => (
          <div
            key={role}
            className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-[14px] font-medium text-navy hover:border-aqua/50 hover:bg-aqua/5 transition-colors flex items-center gap-3"
            data-testid={`role-${role.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-aqua" />
            {role}
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-3xl bg-aqua/5 border border-aqua/30 p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <p className="text-[15px] text-navy max-w-2xl">
          <strong className="font-semibold">Already have someone in India you want to hire?</strong> Bright EOR can help with the employment and onboarding structure.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-aqua hover:text-navy transition-colors"
          data-testid="roles-cta"
        >
          Speak to Bright EOR <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

/* ================= INCLUDED WITH EVERY HIRE ================= */
const IncludedWithHire = () => (
  <section className="bg-navy text-white py-20 lg:py-28 relative overflow-hidden" data-testid="included-with-hire">
    <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
    <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-aqua/10 blur-3xl" aria-hidden />
    <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="grid gap-14 lg:grid-cols-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Included with every hire</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            More than EOR. A managed <span className="text-aqua">offshore support layer.</span>
          </h2>
          <p className="mt-6 text-lg text-white/75 leading-relaxed">
            Every Bright EOR hire comes with the full setup baked in — hardware, connectivity, employment, HR, wellbeing and ongoing management.
          </p>

          <div className="mt-8 rounded-2xl border border-aqua/40 bg-aqua/10 p-5">
            <div className="flex items-start gap-4">
              <div className="mt-1 w-10 h-10 rounded-xl bg-aqua text-navy flex items-center justify-center shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-aqua font-semibold">Optional add-on</p>
                <p className="mt-1 font-display text-lg font-semibold">{ADD_ON.name}</p>
                <p className="mt-1 text-[14.5px] text-white/85 leading-relaxed">{ADD_ON.body}</p>
                <p className="mt-3 inline-flex items-baseline gap-1">
                  <span className="font-display text-2xl font-semibold text-aqua">{ADD_ON.price}</span>
                  <span className="text-sm text-white/70">{ADD_ON.unit}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 lg:p-8">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-aqua" />
                </div>
                <span className="ml-3 text-xs font-medium tracking-tight text-white/60">Bright EOR · Managed Layer</span>
              </div>
              <ClipboardList className="h-4 w-4 text-aqua" />
            </div>
            <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
              {INCLUDED_WITH_HIRE.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13.5px] text-white/90" data-testid={`included-${item.replace(/[^a-z0-9]/gi, "-").toLowerCase().slice(0, 40)}`}>
                  <Check className="h-4 w-4 text-aqua mt-0.5 shrink-0" strokeWidth={2.5} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ================= PROGRESSION (Start with one, scale) ================= */
const Progression = () => (
  <section className="py-20 lg:py-28" data-testid="progression">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Start with one. Scale when it works.</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          One dedicated hire today. A full function <span className="text-aqua">when you&apos;re ready.</span>
        </h2>
        <p className="mt-6 text-lg text-slateink leading-relaxed">
          Single-hire EOR is still the core of what we do. You can begin with one dedicated employee and grow into a team, a complete offshore function or a dedicated India office — at your pace.
        </p>
      </div>
      <div className="relative mt-12">
        <div className="hidden lg:block absolute top-8 left-[8%] right-[8%] h-px border-t-2 border-dashed border-aqua/30" aria-hidden />
        <div className="grid gap-6 lg:grid-cols-4">
          {PROGRESSION.map((p) => (
            <div key={p.stage} className="relative" data-testid={`progression-${p.stage.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="w-16 h-16 rounded-2xl bg-navy text-aqua font-display text-lg font-semibold flex items-center justify-center shadow-card-soft">
                {p.stage.replace("Stage ", "")}
              </div>
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-card-soft hover:border-aqua/40 transition-all">
                <h3 className="font-display text-lg font-semibold text-navy tracking-tight">{p.title}</h3>
                <p className="mt-2 text-[14px] text-slateink leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ================= MANAGED TEAMS TEASER ================= */
const teamTeaserIcons = [Briefcase, Calculator, HeadphonesIcon, LineChart];
const ManagedTeamsTeaser = () => (
  <section className="py-20 lg:py-28" data-testid="managed-teams-teaser">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Managed teams</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
            Not just individual hires. <span className="text-aqua">Complete teams</span> built around your business.
          </h2>
          <p className="mt-5 text-lg text-slateink leading-relaxed">
            We recruit for your exact requirements, you interview and select, and your team works exclusively for you — never shared across other clients.
          </p>
        </div>
        <Link to="/managed-teams" className="group inline-flex items-center gap-2 text-navy font-medium hover:text-aqua transition-colors" data-testid="managed-teams-cta">
          Explore Managed Teams <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {EXAMPLE_TEAMS.map((t, i) => {
          const Icon = teamTeaserIcons[i] || Briefcase;
          return (
            <div key={t.name} className="rounded-3xl bg-white border border-slate-200 p-7 hover:border-aqua/40 hover:-translate-y-1 hover:shadow-card-hover transition-all" data-testid={`home-team-${i}`}>
              <div className="w-11 h-11 rounded-2xl bg-aqua/15 text-navy flex items-center justify-center"><Icon className="h-5 w-5" /></div>
              <h3 className="font-display mt-5 text-lg font-semibold text-navy tracking-tight leading-snug">{t.name}</h3>
              <ul className="mt-4 space-y-1.5">
                {t.roles.slice(0, 5).map((r) => (
                  <li key={r} className="flex items-start gap-2 text-[13.5px] text-slateink"><span className="mt-1.5 w-1 h-1 rounded-full bg-aqua shrink-0" />{r}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ================= OFFICE SETUP ================= */
const OfficeSetup = () => (
  <section className="bg-navy-50 py-20 lg:py-28" data-testid="office-setup">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="grid gap-14 lg:grid-cols-12 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Beyond remote</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
            Want a dedicated team or <span className="text-aqua">office in India?</span>
          </h2>
          <p className="mt-6 text-lg text-slateink leading-relaxed">
            Where clients need a physical office, dedicated team room or a full on-ground presence — we can set it up and run it through our company, so you don&apos;t need to open your own Indian entity.
          </p>
          <p className="mt-4 text-lg text-slateink leading-relaxed">
            Or, if you&apos;d rather stand up your own entity down the line, we&apos;ll walk you through it and help you set it up properly.
          </p>
          <Link
            to="/contact"
            data-testid="office-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-[15px] font-medium text-white hover:bg-aqua hover:text-navy transition-all"
          >
            Discuss office setup <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-xs text-slatemuted">Enquiry-only. Priced per project.</p>
        </div>

        <div className="lg:col-span-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { title: "Managed office via Bright EOR", body: "No entity required on your side. We run the space, IT and people." },
              { title: "Or your own Indian entity", body: "Full setup guidance — incorporation, registrations, compliance." },
              { title: "Workspace, IT & connectivity", body: "Seats, backup power, secure internet, hardware refresh cycle." },
              { title: "Local hiring & scaling", body: "Grow the team from 1 to 20+ without HR/legal burden on your side." },
              { title: "On-ground management", body: "Bright EOR runs the day-to-day so your UK team stays focused." },
              { title: "Enquiry-only pricing", body: "Every setup is different — we scope and quote per project." },
            ].map((o) => (
              <div key={o.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-aqua/15 shrink-0">
                    <Check className="h-3.5 w-3.5 text-aqua" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-navy tracking-tight">{o.title}</p>
                    <p className="mt-1 text-[13.5px] text-slateink leading-relaxed">{o.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ================= PRICING ================= */
const PricingSec = () => (
  <section className="py-20 lg:py-28" data-testid="pricing">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Pricing</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          One clear monthly price for India-based hiring.
        </h2>
        <p className="mt-6 text-lg text-slateink leading-relaxed">
          Dedicated India-based professionals from £895/month, provided as a single all-inclusive figure. No itemised salary, no hidden margin — one price covering the role, equipment, employment costs and managed support.
        </p>
      </div>

      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        {PRICING.map((tier) => (
          <div
            key={tier.name}
            className={`relative rounded-3xl p-8 lg:p-10 border transition-all ${
              tier.highlight
                ? "bg-navy text-white border-navy shadow-card-hover"
                : "bg-white border-slate-200 shadow-card-soft"
            }`}
            data-testid={`pricing-card-${tier.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "")}`}
          >
            {tier.highlight && (
              <div className="absolute top-6 right-6 rounded-full bg-aqua text-navy px-3 py-1 text-xs font-semibold tracking-tight">
                All-inclusive
              </div>
            )}
            <h3 className={`font-display text-2xl font-semibold ${tier.highlight ? "text-white" : "text-navy"}`}>{tier.name}</h3>
            <p className={`mt-2 text-[14px] ${tier.highlight ? "text-white/70" : "text-slatemuted"}`}>{tier.subtitle}</p>
            <div className="mt-6 flex items-baseline gap-2">
              <span className={`font-display text-5xl font-semibold ${tier.highlight ? "text-aqua" : "text-navy"}`}>{tier.price}</span>
              <span className={`text-sm ${tier.highlight ? "text-white/70" : "text-slatemuted"}`}>{tier.unit}</span>
            </div>
            <ul className="mt-8 space-y-3">
              {tier.includes.map((inc) => (
                <li key={inc} className={`flex items-start gap-2.5 text-[14.5px] ${tier.highlight ? "text-white/90" : "text-slateink"}`}>
                  <Check className="h-4 w-4 text-aqua mt-0.5 shrink-0" strokeWidth={2.5} />
                  {inc}
                </li>
              ))}
            </ul>
            <Link
              to={tier.highlight ? "/contact" : "/pricing"}
              data-testid={`pricing-cta-${tier.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "")}`}
              className={`group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-medium transition-all ${
                tier.highlight
                  ? "bg-aqua text-navy hover:shadow-aqua-glow"
                  : "bg-navy text-white hover:bg-aqua hover:text-navy"
              }`}
            >
              {tier.highlight ? "Request an Indicative Cost" : "See full pricing"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        ))}
      </div>

      <p className="mt-8 text-sm text-slatemuted max-w-3xl">
        Final pricing depends on the role, experience level, working hours and employee selected. Your quote is provided as one all-inclusive monthly figure. See the <Link to="/pricing" className="text-navy underline hover:text-aqua">full pricing page</Link> for indicative role ranges, setup fees and managed-team pricing.
      </p>
    </div>
  </section>
);

/* ================= WHY BRIGHT EOR ================= */
const WhyBright = () => (
  <section className="bg-navy-50 py-20 lg:py-28" data-testid="why-bright">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Why Bright EOR</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          Built by someone who understands UK expectations <span className="text-aqua">and Indian delivery.</span>
        </h2>
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {WHY.map((w, i) => {
          const Icon = [Globe2, Handshake, Award, Compass, ShieldCheck, Sparkles][i];
          return (
            <div
              key={w.title}
              className="rounded-3xl bg-white border border-slate-200 p-7 hover:border-aqua/40 hover:-translate-y-1 hover:shadow-card-hover transition-all"
              data-testid={`why-card-${i}`}
            >
              <div className="w-10 h-10 rounded-xl bg-aqua/15 text-navy flex items-center justify-center">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-navy tracking-tight">{w.title}</h3>
              <p className="mt-2 text-[14px] text-slateink leading-relaxed">{w.body}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-10">
        <Link
          to="/who-we-are"
          className="group inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-[15px] font-medium text-white hover:bg-aqua hover:text-navy transition-all"
          data-testid="why-meet-team"
        >
          Meet the founders
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    </div>
  </section>
);

/* ================= INDIA ADVANTAGE ================= */
const IndiaAdvantage = () => (
  <section className="py-20 lg:py-28" data-testid="india-advantage">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="grid gap-12 lg:grid-cols-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">The India advantage</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
            Why <span className="text-aqua">India?</span>
          </h2>
          <p className="mt-6 text-lg text-slateink leading-relaxed">
            India offers UK businesses access to a large, skilled and ambitious talent pool across sales support, admin, customer service, recruitment, marketing, technology and operations.
          </p>
          <p className="mt-4 text-lg text-slateink leading-relaxed">
            With the right structure, UK companies can build high-quality remote teams in India while keeping costs clear and operations professionally managed.
          </p>
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-4 sm:grid-cols-2">
            {INDIA_ADVANTAGE.map((line, i) => (
              <div
                key={line}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-aqua/40 transition-colors"
                data-testid={`india-adv-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-aqua/15 text-navy shrink-0">
                    <Check className="h-3.5 w-3.5 text-aqua" strokeWidth={2.5} />
                  </div>
                  <p className="text-[15px] text-slateink leading-relaxed">{line}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ================= BRAND ECOSYSTEM ================= */
const BrandEcosystem = () => (
  <section className="bg-navy-50 py-20 lg:py-28" data-testid="ecosystem">
    <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Brand ecosystem</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          Part of Brightridge Partners.
        </h2>
        <p className="mt-6 text-lg text-slateink leading-relaxed">
          Bright EOR sits under the Brightridge Partners umbrella alongside specialist service brands built to help UK businesses hire, grow and operate smarter.
        </p>
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {ECOSYSTEM.map((b) => {
          const inner = (
            <>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${b.current ? "bg-aqua text-navy" : "bg-aqua/15 text-navy"}`}>
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className={`font-display mt-6 text-xl font-semibold tracking-tight ${b.current ? "text-white" : "text-navy"}`}>{b.name}</h3>
              <p className={`mt-2 text-[14px] leading-relaxed ${b.current ? "text-white/75" : "text-slateink"}`}>{b.body}</p>
              {b.current ? (
                <p className="mt-6 text-xs uppercase tracking-[0.22em] text-aqua font-semibold">You are here</p>
              ) : (
                <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-navy group-hover:text-aqua transition-colors">
                  Visit site <ExternalLink className="h-3 w-3" />
                </span>
              )}
            </>
          );
          const base = `group rounded-3xl p-8 border transition-all ${
            b.current
              ? "bg-navy text-white border-navy shadow-card-hover"
              : "bg-white border-slate-200 hover:border-aqua/40 hover:-translate-y-1 hover:shadow-card-hover"
          }`;
          return b.url ? (
            <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" className={base} data-testid={`ecosystem-${b.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {inner}
            </a>
          ) : (
            <div key={b.name} className={base} data-testid={`ecosystem-${b.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ================= FAQ ================= */
const FaqSec = () => (
  <section className="py-20 lg:py-28" data-testid="faq">
    <div className="mx-auto max-w-4xl px-6 md:px-10 lg:px-12">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Frequently asked</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          Questions before you begin.
        </h2>
      </div>

      <div className="mt-12">
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-slate-200" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-display text-[17px] font-medium text-navy hover:no-underline hover:text-aqua py-5">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-slateink leading-relaxed pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <div className="mt-12 text-center">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 text-navy font-medium hover:text-aqua transition-colors"
          data-testid="faq-cta"
        >
          Still have questions? Speak to Bright EOR <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

export default Home;
